import { JurisdictionContext, URLS, createJurisdictionHeaders } from '@/data';
import { useJurisdictionHeaderValue } from '@/data/reactQueryClient';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { PaginationMeta } from '@/types/pagination';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { Bet, GetBetsQueryParams, GetBetsResponse, OPEN_BET_TABS, UseInfiniteBetsQueryOptions } from './types';

/**
 * Get all bets
 * GET /auth/bets
 */
export const getAllBets = async (
    params: GetBetsQueryParams,
    context: JurisdictionContext = {}
): Promise<GetBetsResponse> => {
    const { actions } = useEventDetailsCache.getState();
    const queryParams = new URLSearchParams();
    if (params.page) {
        queryParams.append('page', params.page.toString());
    }
    if (params.tab) {
        queryParams.append('tab', params.tab);
    }

    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/bets?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(context),
            'Content-Type': 'application/json',
        },
    });
    const data = await resp.json();
    if (!resp.ok) {
        throw data;
    }

    if (OPEN_BET_TABS.includes(params.tab)) {
        actions.populate(
            data?.data?.flatMap((bet: Bet) =>
                bet.events.map(event => ({
                    ...(event.event_details ?? {}),
                    id: event.id,
                    status: event.status,
                }))
            )
        );
    }

    return data;
};

export const betKeys = {
    all: ['bet'] as const,
    infiniteLists: () => [...betKeys.all, 'infinite-list'] as const,
    infiniteList: (params: GetBetsQueryParams, jurisdiction: string) =>
        [...betKeys.infiniteLists(), { ...params, jurisdiction }] as const,
    details: () => [...betKeys.all, 'detail'] as const,
    detail: (id: string) => [...betKeys.details(), id] as const,
    cashoutAmounts: () => [...betKeys.all, 'cashout-amounts'] as const,
    cashoutAmount: (id: string) => [...betKeys.cashoutAmounts(), id] as const,
};

/**
 * React query infinite query to get all bets
 */
export function useInfiniteBetsQuery<TData>(params: GetBetsQueryParams, options?: UseInfiniteBetsQueryOptions<TData>) {
    const jurisdiction = useJurisdictionHeaderValue();

    return useInfiniteQuery({
        queryKey: betKeys.infiniteList({ ...params }, jurisdiction),
        queryFn: async ({ pageParam = 1 }) => await getAllBets({ ...params, page: pageParam as number }),
        initialPageParam: 1,
        getNextPageParam,
        ...options,
    });
}

/**
 * Total number of live entries, driving the count badge on the Live tab. Shares the `tab: 'Live'`
 * query key with the Live `BetListContent`, so it adds no extra request, it just reads `meta.total`
 * off the first page regardless of which tab is currently focused. Falls back to 0 when the BE
 * returns a body without `meta`.
 */
export function useLiveEntriesCount(): number {
    const { data } = useInfiniteBetsQuery<InfiniteData<GetBetsResponse, unknown>>({ tab: 'Live' });
    return data?.pages[0]?.meta?.total ?? 0;
}

/**
 * helper function to get the next page param
 */
export const getNextPageParam = (resp: { meta?: PaginationMeta }) => {
    if (!resp.meta) {
        return undefined;
    }
    return resp.meta.current_page < resp.meta.last_page ? resp.meta.current_page + 1 : undefined;
};
